import { QueryClient } from "@tanstack/react-query";
import { Container, Box, Typography, Table, TableRow, TableCell, TableBody, Button, Alert, Card, CardContent, AlertTitle, Chip, Accordion, AccordionDetails, AccordionSummary, AccordionActions } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { useMemo } from "react";
import { apiClient, apiClientHooks } from "../bootstrapping/InitApiClient";
import { LoaderFunction, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ShowInvites from "../components/invites/ShowInvites";
import { useIdTokenClaims } from "../components/auth0/useIdTokenClaims";
import { schemas } from "../generated/api/client";
import { z } from "zod";
import { applicationStatusMapping } from "../utils/option-data";
import WithdrawApplicationButton from "../components/WithdrawApplicationButton";
import RestartApplicationButton from "../components/RestartApplicationButton";

export const loader = (queryClient: QueryClient): LoaderFunction => async () => {
  // Build the queryKey for `apiClientHooks.getMyEBulkApplications()`
  const queryKey = [...apiClientHooks.getKeyByAlias('getMyEBulkApplications'), {}]
  // Ensure the query is in the cache
  const eBulkApplications = await queryClient.ensureQueryData({ queryKey, queryFn: () => apiClient.getMyEBulkApplications() })
  // Return the data, for consumption with `useLoaderData()`
  // You can type it with: `as Awaited<ReturnType<ReturnType<typeof loader>>>` but that risks it not being corect
  return eBulkApplications
}

type EBulkApplication = z.infer<typeof schemas.EBulkApplication>

export const sections: Array<{
  label: string;
  path: string;
  isComplete: (dbsApp: EBulkApplication) => boolean;
}> = [
    {
      label: 'Identity',
      path: 'identity',
      isComplete: dbsApp =>
        !!dbsApp.data.AdditionalApplicantDetails?.LanguagePreference &&
        !!dbsApp.data.Gender &&
        !!dbsApp.data.NiNumber,
    },
    {
      label: 'Names',
      path: 'names',
      isComplete: dbsApp =>
        !!dbsApp.data.Forename &&
        !!dbsApp.data.PresentSurname,
    },
    {
      label: 'Birth',
      path: 'birth',
      isComplete: dbsApp =>
        !!dbsApp.data.DateOfBirth &&
        !!dbsApp.data.Gender,
    },
    {
      label: 'Addresses',
      path: 'addresses',
      isComplete: dbsApp =>
        !!dbsApp.data.CurrentAddress,
    },
    {
      label: 'Documents',
      path: 'documents',
      isComplete: dbsApp =>
        !!dbsApp.data.ApplicantIdentityDetails?.PassportDetails?.PassportNumber ||
        !!dbsApp.data.ApplicantIdentityDetails?.DriverLicenceDetails?.DriverLicenceNumber ||
        !!dbsApp.data.NonApplicationFields?.NoPassportOrDriverLicence,
    },
  ]

type EBulkApplicationSectionsProps = {
  dbsApp: EBulkApplication;
  readonly?: boolean;
}

const EBulkApplicationSections: React.FC<EBulkApplicationSectionsProps> = ({ dbsApp, readonly = false }) => {
  const navigate = useNavigate()

  return <>
    {sections.map(section => (
      <Typography key={section.path} variant="body2" color="text.secondary" sx={{ marginTop: 2 }}>
        {
          readonly === true
            ? '🔒'
            : section.isComplete(dbsApp)
              ? '✅'
              : '❌'
        }
        <Button data-test-id={`update-${section.path}`} onClick={() => navigate(`/applications/${dbsApp.id}/${section.path}`)}>
          {readonly
            ? 'View'
            : section.isComplete(dbsApp)
              ? 'Update'
              : 'Input'}
        </Button>
        {section.label}
      </Typography>
    ))}
    {!readonly && <Button sx={{ marginTop: 1 }} variant='contained' onClick={() => { navigate(`/applications/${dbsApp.id}/submit`) }}>Submit Application</Button>}
    <WithdrawApplicationButton sx={{ marginLeft: 1, marginTop: 1 }} variant='contained' color='error' dbsApp={dbsApp}>
      Withdraw Application
    </WithdrawApplicationButton>
  </>
}

type EBulkApplicationMeetingProps = {
  dbsApp: EBulkApplication;
}

const EBulkApplicationMeeting: React.FC<EBulkApplicationMeetingProps> = ({ dbsApp }) => {
  const navigate = useNavigate()

  const hasSelectedDocuments = dbsApp.documents_applicant == null
  const selectedDocumentCount = useMemo(() => {
    return Object.values(dbsApp.documents_applicant?.group1 ?? {}).filter(each => each.selected).length
      + Object.values(dbsApp.documents_applicant?.group2a ?? {}).filter(each => each.selected).length
      + Object.values(dbsApp.documents_applicant?.group2b_last3mo ?? {}).filter(each => each.selected).length
      + Object.values(dbsApp.documents_applicant?.group2b_last12mo ?? {}).filter(each => each.selected).length
      + Object.values(dbsApp.documents_applicant?.group2b_anyTime ?? {}).filter(each => each.selected).length
  }, [dbsApp])

  return <>
    <Table aria-label="simple table">
      <TableBody>
        <TableRow>
          <TableCell style={{ paddingLeft: 0, paddingRight: 0, border: 'none' }}>
            <Typography variant="h4" color="text.secondary" sx={{ marginRight: 2 }}>
              📅
            </Typography>
          </TableCell>
          <TableCell style={{ paddingLeft: 0, paddingRight: 0 }}>
            <Typography variant="body1" color="text.secondary">
              {hasSelectedDocuments
                ? <>
                  <strong>Prepare for your application to be reviewed</strong><br />
                  You now need to select the documents you will bring with you<br />
                </>
                : <>
                  <strong>Your employer will now contact you to arrange a meeting to review your application</strong><br />
                  You have selected the documents you will bring with you, but you can still update them below<br />
                </>}
            </Typography>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
    <Button sx={{ marginTop: 1 }} variant="contained" onClick={() => { navigate(`/applications/${dbsApp.id}/meeting`) }}>
      {hasSelectedDocuments ? 'Select Documents to Bring' : `Update Documents to Bring (${selectedDocumentCount} selected)`}
    </Button>
  </>
}

type EBulkApplicationPaymentProps = {
  dbsApp: EBulkApplication;
}

const EBulkApplicationPayment: React.FC<EBulkApplicationPaymentProps> = ({ dbsApp }) => {
  const navigate = useNavigate()

  const isPaid = dbsApp.payment_method != null

  return isPaid
    ? <>
      <Alert sx={{ marginTop: 1 }} color="success">
        <AlertTitle>Application Paid</AlertTitle>
        The application should be processed soon.
      </Alert>
      <Button sx={{ marginTop: 1 }} disabled={true}>
        ✅ Paid
      </Button>
    </>
    : <>
      <Alert sx={{ marginTop: 1 }} color="warning">
        <AlertTitle>Payment Due</AlertTitle>
        If your employer is <strong>not</strong> paying for this application, you can pay for it below.
      </Alert>
      <Button sx={{ marginTop: 1 }} onClick={() => { navigate(`/applications/${dbsApp.id}/payment`) }}>
        💳 Pay Now
      </Button>
    </>
}



export const EBulkApplicationCard: React.FC<{ dbsApp: EBulkApplication }> = ({ dbsApp }) => {
  const navigate = useNavigate()

  const allSectionsComplete = useMemo(() => {
    return sections.every(section => section.isComplete(dbsApp))
  }, [dbsApp])

  return (
    <Card key={dbsApp.id} style={{ marginBottom: '20px' }} variant="outlined">
      <CardContent>
        <Typography variant="h5" component="div">
          {dbsApp.test_application && <span style={{ color: 'red' }}>🚨 TEST </span>}
          Application: <strong>{dbsApp.job_role}</strong> at <strong>{dbsApp.customer_name}</strong>
        </Typography>
        {/* <Chip
          icon={<ChipIcon />}
          label={applicationStatusMapping[dbsApp.status].label}
          sx={{ marginTop: 0.5 }}
          {...applicationStatusMapping[dbsApp.status].chipIconProps}
        /> */}
        {/* Vary the body details and actions available based on status */}
        {/* Draft: show a list of sections, and submission button */}
        {dbsApp.status === 'draft' && <>
          {allSectionsComplete
            ? (
              <Alert sx={{ marginTop: 1 }} color="info">
                <AlertTitle>All Sections Complete</AlertTitle>
                You have completed all sections of the application. You can now submit your application.
              </Alert>
            )
            : (
              <Alert sx={{ marginTop: 1 }} color="info">
                <AlertTitle>Incomplete Application</AlertTitle>
                Please complete and submit your application to proceed.
              </Alert>
            )}
          <EBulkApplicationSections dbsApp={dbsApp} />
        </>}
        {/* Awaiting Customer: advise applicant to contact customer and arrange a meeting; can still view the submission; button to show the page where they can identify the documents they need to bring */}
        {dbsApp.status === 'awaiting_customer_approval' && <>
          <EBulkApplicationSections dbsApp={dbsApp} readonly={true} />
          {dbsApp.can_applicant_select_documents && (
            <EBulkApplicationMeeting dbsApp={dbsApp} />
          )}
        </>}
        {/* Awaiting Automated Verification: give applicant the option of automatically complete */}
        {dbsApp.status === 'awaiting_automated_verification' && <>
          <EBulkApplicationSections dbsApp={dbsApp} readonly={true} />
          <Button sx={{ marginTop: 1 }} variant='contained' onClick={() => { navigate(`/applications/${dbsApp.id}/automated-verification`) }}>Verify your Identity</Button>
        </>}
        {/* Awaiting Payment: hide the list of sections, but allow viewing of the submission (no editing) ++ add a payment button */}
        {dbsApp.status === 'awaiting_payment' && <>
          {!dbsApp.can_applicant_pay && (
            <Alert sx={{ marginTop: 1 }} color="info">
              <AlertTitle>Payment by Employer</AlertTitle>
              Your employer is handling the payment for this application. No action is required from you at this time.
            </Alert>
          )}
          <EBulkApplicationSections dbsApp={dbsApp} readonly={true} />
          {dbsApp.can_applicant_pay && (
            <EBulkApplicationPayment dbsApp={dbsApp} />
          )}
        </>}
        {/* Needs Changes: view only submission, state: needs changes */}
        {dbsApp.status === 'needs_changes' && <>
          <Alert sx={{ marginTop: 1 }} color="warning">
            <AlertTitle>Changes Required</AlertTitle>
            Your application needs changes before it can proceed. Please contact your employer for more details.
          </Alert>
        </>}
        {/* Awaiting Staff: view only submission, state: awaiting countersign */}
        {dbsApp.status === 'awaiting_countersigning' && <>
          <Alert sx={{ marginTop: 1 }} color="info">
            <AlertTitle>Awaiting Countersignature</AlertTitle>
            Total Screening is reviewing your application and will contact you if any further information is required.
          </Alert>
        </>}
        {/* Awaiting DBS: view only submission, state: ready to send to DBS */}
        {dbsApp.status === 'awaiting_submission_to_DBS' && <>
          <Alert sx={{ marginTop: 1 }} color="info">
            <AlertTitle>Preparing Application</AlertTitle>
            Your application is being prepared for submission to the DBS. Total Screening will contact you if any further information is required.
          </Alert>
        </>}
        {/* Awaiting Response DBS: view only submission, state: submitted to DBS, awaiting response */}
        {dbsApp.status === 'awaiting_response_from_DBS' && <>
          <Alert sx={{ marginTop: 1 }} color="info">
            <AlertTitle>Application Submitted</AlertTitle>
            Your application has been submitted to the DBS. You will be notified once the DBS has processed your application.
          </Alert>
        </>}
        {/* Error from DBS: view only submission, state: with the DBS, show the DBS ID */}
        {dbsApp.status === 'error_from_DBS' && <>
          <Alert sx={{ marginTop: 1 }} color="error">
            <AlertTitle>Issue with DBS Submission</AlertTitle>
            There was an issue submitting your application to the DBS. Total Screening will contact you to resolve this.
          </Alert>
        </>}
        {/* Completed: view only submission, show the DBS ID */}
        {dbsApp.status === 'completed' && <>
          <Alert sx={{ marginTop: 1 }} color="success">
            <AlertTitle>Application Complete</AlertTitle>
            Your application has been completed. You will receive your DBS certificate by post.
          </Alert>
        </>}
        {/* Restarted: view only submission, show that the application has been restarted */}
        {dbsApp.status === 'restarted' && <>
          <Alert sx={{ marginTop: 1 }} color="info">
            <AlertTitle>Application Restarted</AlertTitle>
            An invite for a new application has been created. You can see your old application details below.
          </Alert>
          <EBulkApplicationSections dbsApp={dbsApp} readonly={true} />
        </>}
        {/* Rejected: view only submission, show reason */}
        {dbsApp.status === 'rejected' && <>
          <Alert sx={{ marginTop: 1 }} color="error" icon={false} test-id="rejection-reason">
            {dbsApp.rejection_reason}
            <Typography variant="body2" color="text.secondary" sx={{ pt: 0.5 }}>
              <em>by {
                dbsApp.rejected_by === 'staff'
                  ? 'Registered Body'
                  : dbsApp.rejected_by === 'customer'
                    ? dbsApp.customer_name
                    : dbsApp.rejected_by === 'applicant'
                      ? 'applicant'
                      : dbsApp.rejected_by
              }</em>
            </Typography>
            <RestartApplicationButton dbsApp={dbsApp} sx={{ marginTop: 1 }} variant='contained'>
              Restart Application
            </RestartApplicationButton>
          </Alert>
          <EBulkApplicationSections dbsApp={dbsApp} readonly={true} />
        </>}
        {/* Track an application at any stage that has an E-Number and a Date of Birth */}
        {dbsApp.dbs_enumber && dbsApp.data.DateOfBirth && (
          <div>
            <form action="https://secure.crbonline.gov.uk/enquiry/enquirySearchAction.do" method="POST" target="_blank">
              <input type="hidden" name="applicationNo" value={dbsApp.dbs_enumber} />
              <input type="hidden" name="dateOfBirthDay" value={new Date(dbsApp.data.DateOfBirth).getDate().toString().padStart(2, '0')} />
              <input type="hidden" name="dateOfBirthMonth" value={new Date(dbsApp.data.DateOfBirth).getMonth() + 1} />
              <input type="hidden" name="dateOfBirthYear" value={new Date(dbsApp.data.DateOfBirth).getFullYear()} />
              <Button
                sx={{ marginTop: 1 }}
                variant='contained'
                type='submit'
              >
                Track Application on DBS Website
              </Button>
            </form>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export const ListApplicationsByStatus: React.FC<{ applications: EBulkApplication[] }> = ({ applications }) => {
  const applicationsByStatus = useMemo((): Record<EBulkApplication['status'], EBulkApplication[]> => ({
    draft: applications.filter(app => app.status === 'draft'),
    awaiting_customer_approval: applications.filter(app => app.status === 'awaiting_customer_approval'),
    awaiting_automated_verification: applications.filter(app => app.status === 'awaiting_automated_verification'),
    awaiting_payment: applications.filter(app => app.status === 'awaiting_payment'),
    needs_changes: applications.filter(app => app.status === 'needs_changes'),
    awaiting_countersigning: applications.filter(app => app.status === 'awaiting_countersigning'),
    awaiting_submission_to_DBS: applications.filter(app => app.status === 'awaiting_submission_to_DBS'),
    awaiting_response_from_DBS: applications.filter(app => app.status === 'awaiting_response_from_DBS'),
    in_progress: applications.filter(app => app.status === 'in_progress'),
    completed: applications.filter(app => app.status === 'completed'),
    restarted: applications.filter(app => app.status === 'restarted'),
    error_from_DBS: applications.filter(app => app.status === 'error_from_DBS'),
    rejected: applications.filter(app => app.status === 'rejected'),
    withdrawn: applications.filter(app => app.status === 'withdrawn'),
  }), [applications])

  const defaultExpandedStatuses: EBulkApplication['status'][] = [
    'draft',
    'awaiting_customer_approval',
    'awaiting_automated_verification',
    'awaiting_payment',
    'in_progress',
    'completed',
  ]

  return <div>
    {Object.entries(applicationsByStatus)
      // Withdrawn is rare (DBS-248) — only show the section to applicants it affects
      .filter(([status, apps]) => status !== 'withdrawn' || apps.length > 0)
      .map(([status, apps]) => {
      const typedStatus = status as keyof typeof applicationStatusMapping
      const ChipIcon = applicationStatusMapping[typedStatus].Icon

      const expandedByDefault = apps.length > 0 && defaultExpandedStatuses.includes(typedStatus)

      return (
        <Accordion key={status} defaultExpanded={expandedByDefault}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`panel-${status}-content`}
            id={`panel-${status}-header`}
          >
            <Chip
              icon={<ChipIcon />}
              label={`${applicationStatusMapping[typedStatus].label} (${apps.length})`}
              sx={{ marginTop: 0.5 }}
              {...applicationStatusMapping[typedStatus].chipIconProps}
            />
          </AccordionSummary>
          {apps.length === 0 && (
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary">
                You have no applications in this status.
              </Typography>
            </AccordionDetails>
          )}
          {apps.map(app => (
            <AccordionDetails key={app.id}>
              <EBulkApplicationCard dbsApp={app} />
            </AccordionDetails>
          ))}
        </Accordion>
      )
    })}
  </div>
}

export const Element: React.FC = () => {
  const { email } = useIdTokenClaims()

  // NOTE: We could reuse the data from the loader, but we would have to use "as" to cast the type, and risk it not being correct
  // const eBulkApplications = useLoaderData() as Awaited<ReturnType<ReturnType<typeof loader>>>
  // Instead we just use the hook again, which should serve data from the cache
  const getMyEBulkApplications = apiClientHooks.useGetMyEBulkApplications()

  return <>
    <Helmet>
      <title>Your DBS Applications</title>
    </Helmet>
    <Container component="main">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography component="h1" variant="h5" marginBottom={3}>
          Your DBS Applications
        </Typography>
        <ShowInvites
          noInvites={
            !getMyEBulkApplications.data?.length
              ? <>
                <Alert severity="error" sx={{ marginBottom: 3 }}>
                  You have <strong>no applications</strong>. You need to be <strong>invited by your company</strong> to apply for a DBS check.
                  <br />
                  <br />
                  Please request an <strong>applicant job invite</strong> for: <strong>{email}</strong>.
                </Alert>
              </>
              : null
          }
        />
        {getMyEBulkApplications.data && <ListApplicationsByStatus applications={getMyEBulkApplications.data} />}
      </Box>
    </Container>
  </>
}